/*
 * Copyright (c) 2012-present Sedicii Innovations Ltd.
 *
 * All rights reserved.
 */

import { ProverProtocol } from '@sedicii/zkp-protocol-js'

import configuration from '../configuration'

const LOGIN_MAX_INTENT_POOLING_COUNT = 60
const LOGIN_POOLING_INTERVAL = 1000 * 2

export const KYC_LOGIN_RESET = 'KYC_LOGIN_RESET'
export const KYC_LOGIN_FORM_SUBMIT = 'KYC_LOGIN_FORM_SUBMIT'
export const KYC_LOGIN_FORM_ERROR = 'KYC_LOGIN_FORM_ERROR'
export const KYC_LOGIN_MFA_PENDING = 'KYC_LOGIN_MFA_PENDING'
export const KYC_LOGIN_MFA_SUCCESS = 'KYC_LOGIN_MFA_SUCCESS'
export const KYC_LOGIN_MFA_FAILURE = 'KYC_LOGIN_MFA_FAILURE'
export const KYC_LOGIN_LOGOUT = 'KYC_LOGIN_LOGOUT'
export const KYC_LOGIN_SYNC = 'KYC_LOGIN_SYNC'

export function loginReset () {
  return dispatch => {
    dispatch({ type: KYC_LOGIN_RESET })
  }
}

export function loginSync (payload) {
  return dispatch => {
    dispatch({ type: KYC_LOGIN_SYNC, payload })
  }
}

export function logout () {
  return dispatch => {
    dispatch({ type: KYC_LOGIN_LOGOUT })
  }
}

export function login ({ email, password }) {
  return dispatch => {
    let context = {
      email,
      attributes: [ { id: 'password' } ],
      clearAttributes: [ { id: 'password', secret: password } ]
    }

    const baseURL = '/api'

    return Promise.resolve()
      .then(() => dispatch({ type: KYC_LOGIN_FORM_SUBMIT }))
      .then(() => authorize({ notifyToUser: false }))
      .then(createRequestContext)
      .then(advanceVerificationProcess)
      .then(createProverProtocol)
      .then(attributes => context.proverProtocol.processVerificationParametersResponse(attributes))
      .then(advanceVerificationProcess)
      .then(body => context.proverProtocol.processChallengeCommitmentResponse(body.attributes))
      .then(advanceVerificationProcess)
      .then(body => context.proverProtocol.processChallengeGraphsResponse(body.attributes))
      .then(advanceVerificationProcess)
      .then(() => authorize({ notifyToUser: true }))
      .catch(payload => {
        dispatch({ type: KYC_LOGIN_FORM_ERROR, payload })
        context.loginError = true
      })
      .then((payload) => {
        if (context.loginError) {
          return
        }

        return Promise.resolve(payload)
          .then(payload => dispatch({ type: KYC_LOGIN_MFA_PENDING, payload }))
          .then(poolingMFA)
          .then(payload => {
            dispatch({ type: KYC_LOGIN_MFA_SUCCESS, payload })
          })
          .catch(payload => {
            dispatch({ type: KYC_LOGIN_MFA_FAILURE, payload })
          })
      })

    function createRequestContext ({ idpId, idpIdentityId, access_token: accessToken }) {
      context = {
        idpId,
        idpIdentityId,
        accessToken,
        ...context
      }

      return context.attributes
    }

    function createProverProtocol ({ attributes, challenges, permutationSize, id: idpVerificationId }) {
      context = {
        ...context,

        proverProtocol: ProverProtocol({
          challenges,
          permutationSize,
          attributes: context.clearAttributes,
          identityId: context.idpIdentityId
        }),
        idpVerificationId
      }

      return attributes
    }

    function authorize ({ notifyToUser }) {
      return fetch(`${ baseURL }/auth/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-RP-Id': configuration.RELYING_PARTY_ID
        },
        body: JSON.stringify({
          email: context.email,
          idpVerificationId: context.idpVerificationId,
          notifyToUser: notifyToUser,
          client_id: configuration.CLIENT_ID,
          client_secret: configuration.CLIENT_SECRET
        })
      }).then(fetchStatusHandler)
        .then(response => response.json())
    }

    function advanceVerificationProcess (attributes) {
      return fetch(`${ baseURL }/identity-providers/${ context.idpId }/identities/${ context.idpIdentityId }/verifications/${ context.idpVerificationId || '' }`, {
        method: context.idpVerificationId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${ context.accessToken }`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: attributes,
          client_id: configuration.CLIENT_ID,
          client_secret: configuration.CLIENT_SECRET
        })
      }).then(fetchStatusHandler)
        .then(response => response.json())
    }

    function fetchStatusHandler (response) {
      if (response.status < 400) {
        return response
      } else {
        throw new Error(response.statusText)
      }
    }

    function poolingMFA () {
      return new Promise((resolve, reject) => {
        let currentIntentPooling = 0
        send()

        function send () {
          currentIntentPooling++
          if (currentIntentPooling > LOGIN_MAX_INTENT_POOLING_COUNT) {
            return reject(new Error('Max intent reached'))
          }

          authorize({ notifyToUser: false })
            .then(payload => {
              if (payload.id_token) {
                resolve(payload)
              } else {
                setTimeout(send, LOGIN_POOLING_INTERVAL)
              }
            })
            .catch(reject)
        }
      })
    }

  }
}
